import type { QuantityData, TaskData, TaskType } from "shared/schema";
import type { AsyncValueCallback, ImmutableDictionary } from "shelving";
import { AsyncButton, DialogButton, Form, Icon, NumberField, SubmitButton } from "shared/react";
import { QUANTITY_SCHEMA, formatTaskCompletion, isTaskDone, isUnitType } from "shared/schema";
import { ProfilePhoto } from "../entity/EntityPhoto.js";
import { UnitField } from "../misc/UnitField.js";
import { USER } from "../util/user.js";

/** List of non-interactive tasks with 100% progress. */
export function Tasks({ tasks }: { tasks: ImmutableDictionary<TaskData> }): React.ReactElement {
	return (
		<>
			{Object.entries(tasks).map(([tid, task]) => {
				// Display the task at 100% because it looks better.
				const { soloTarget, sharedTarget } = task;
				return <Task key={tid} id={tid} task={{ ...task, total: sharedTarget }} total={soloTarget} />;
			})}
		</>
	);
}

/**
 * Show a checkbox task, counter task, daily task, weekly task, or tracking task.
 * e.g. "Visit the Eiffel tower" or "Go swimming 50 times"
 */
export function Task({
	id,
	task, //
	total: soloTotal = 0,
	onProgress,
}: {
	id: string;
	task: TaskData;
	total?: number | undefined;
	onProgress?: AsyncValueCallback<QuantityData> | undefined;
}): React.ReactElement {
	const { title, total: sharedTotal, soloTarget, sharedTarget, type, shared } = task;
	const done = isTaskDone(task, soloTotal);
	const buttonMods = `flex-fit ${done ? "button-success" : ""}`;
	return (
		<article className="space-xsmall" data-task={id}>
			<div className="flex-row flex-bottom gap-xsmall">
				<TaskProgress //
					{...task}
					title={shared ? `${title} (everyone)` : title}
					target={shared ? sharedTarget : soloTarget}
					total={shared ? sharedTotal : soloTotal}
					mods="flex-full"
				/>
				{type === "checkbox" || type === "counter" ? (
					<TaskIncrementButton mods={buttonMods} onProgress={onProgress} /> //
				) : (
					<TaskAmountButton task={task} mods={buttonMods} onProgress={onProgress} />
				)}
			</div>
			{shared ? (
				<div className="flex-row flex-bottom gap-xsmall space-xsmall">
					<ProfilePhoto uid={USER.data.uid} mods="size-control" />
					<TaskProgress {...task} title={`${title} (you)`} target={soloTarget} total={soloTotal} />
				</div>
			) : null}
		</article>
	);
}

function TaskProgress({ title, total, target, type, units, mods = "" }: { title: string; total: number; target: number; type: TaskType; units: string | null; mods?: string }): React.ReactElement {
	return (
		<div className={mods}>
			<p className="flex-row flex-bottom size-small space-xxsmall gap-xsmall">
				<strong className="text-left flex-full">{title}</strong>
				<span className="text-nowrap">{formatTaskCompletion(total, target, type, units)}</span>
			</p>
			<progress className="space-none" max={target} value={total} />
		</div>
	);
}

function TaskIncrementButton({ mods, onProgress }: { mods?: string | undefined; onProgress?: AsyncValueCallback<QuantityData> | undefined }): React.ReactElement {
	return (
		<AsyncButton mods={mods} title="Increment task" onClick={onProgress ? () => onProgress({ amount: 1, units: null }) : undefined}>
			<Icon icon="tick" />
		</AsyncButton>
	);
}

function TaskAmountButton({ mods, task, onProgress }: { mods?: string | undefined; task: TaskData; onProgress?: AsyncValueCallback<QuantityData> | undefined }): React.ReactElement {
	return (
		<DialogButton mods={mods} title="Add progress" dialog={onProgress && <TaskAmountModal task={task} onProgress={onProgress} />}>
			<Icon icon="add" />
		</DialogButton>
	);
}

function TaskAmountModal({ task, onProgress }: { task: TaskData; onProgress: AsyncValueCallback<QuantityData> }): React.ReactElement {
	const { type, units } = task;
	return (
		<article className="modal">
			<h2>Add progress</h2>
			<Form data={{ amount: 1, units }} schema={QUANTITY_SCHEMA} onSubmit={data => onProgress(data)} mods="flex-row flex-bottom gap-hairline">
				<NumberField name="amount" title="" mods="flex-half" />
				{isUnitType(type) ? <UnitField name="units" title="" type={type} mods="flex-half" /> : null}
				<SubmitButton title="Add progress">
					<Icon icon="add" />
				</SubmitButton>
			</Form>
		</article>
	);
}
